import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container, Typography, Button, Modal, Box } from '@mui/material';
import 'bootstrap-icons/font/bootstrap-icons.css';
import config from '../../config';
import { encryptData } from '../../utils/cryptoUtils';

const SCHEME_CATEGORIES = [
  { key: 'cargo', label: 'Cargo' },
  { key: 'passenger', label: 'Passenger' },
  { key: 'l3', label: 'L3' },
  { key: 'deviation', label: 'Deviation (L-5)' },
];

const emptyForm = {
  id: '',
  scheme_category: 'cargo',
  oem_id: '',
  customer_category: '',
  city: '',
  type: '',
  tenure: '',
  ltv: '',
  old_rate: '',
  increase: '',
  standard_rate_flat: '',
  standard_rate_reducing: '',
  bureau_score: '',
  bureau_impact: '',
  applicable_rate_flat: '',
  applicable_rate_reducing: '',
  pf: '',
  document_charges: '',
  kli: '',
  irr: '',
  remarks: '',
  effective_from: '',
  effective_to: '',
  status: '1',
};

const SchemeMaster = () => {
  const [schemes, setSchemes] = useState([]);
  const [oems, setOems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [filterCategory, setFilterCategory] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [bureauDropdownOpen, setBureauDropdownOpen] = useState(false);
  const [bureauSearch, setBureauSearch] = useState('');

  const BUREAU_SCORE_OPTIONS = Array.from({ length: (900 - 550) / 25 + 1 }, (_, i) => String(550 + i * 25));

  const userInfo = useSelector((state) => state.userInfo);
  const userId = userInfo?.id;

  // ---- get OEM dropdown list ----
  const getOems = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}get_oem_name_id`);
      const result = await response.json();
      if (result.success) {
        setOems(result.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    getOems();
  }, []);

  // ---- get scheme master list ----
  const getSchemes = useCallback(async () => {
    try {
      setLoading(true);

      const response = await fetch(`${config.API_BASE_URL}get_scheme_master`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData({
          scheme_category: filterCategory,
          length: 100,
          start: 0,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSchemes(Array.isArray(result.data?.data) ? result.data.data : []);
        setError('');
      } else {
        setSchemes([]);
        setError(result.message || 'No records found.');
      }
    } catch (e) {
      setSchemes([]);
      setError('Something went wrong while fetching schemes.');
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  useEffect(() => {
    getSchemes();
  }, [getSchemes]);

  const openAddModal = () => {
    setForm({ ...emptyForm, scheme_category: filterCategory || 'cargo' });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setForm({
      id: row.id,
      scheme_category: row.scheme_category || 'cargo',
      oem_id: row.oem_id || '',
      customer_category: row.customer_category || '',
      city: row.city || '',
      type: row.type || '',
      tenure: row.tenure ?? '',
      ltv: row.ltv ?? '',
      old_rate: row.old_rate ?? '',
      increase: row.increase ?? '',
      standard_rate_flat: row.standard_rate_flat ?? '',
      standard_rate_reducing: row.standard_rate_reducing ?? '',
      bureau_score: row.bureau_score || '',
      bureau_impact: row.bureau_impact ?? '',
      applicable_rate_flat: row.applicable_rate_flat ?? '',
      applicable_rate_reducing: row.applicable_rate_reducing ?? '',
      pf: row.pf ?? '',
      document_charges: row.document_charges || '',
      kli: row.kli || '',
      irr: row.irr ?? '',
      remarks: row.remarks || '',
      effective_from: row.effective_from || '',
      effective_to: row.effective_to || '',
      status: String(row.status ?? '1'),
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm);
    setFormError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ---- add / update scheme ----
  const handleSave = async () => {
    if (!form.scheme_category) {
      setFormError('Scheme category is required.');
      return;
    }

    try {
      setSaving(true);
      setFormError('');

      const payload = {
        ...form,
        user_id: userId,
      };

      const response = await fetch(`${config.API_BASE_URL}masters/cu_scheme_master`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(payload),
      });

      const result = await response.json();

      if (result.success) {
        closeModal();
        getSchemes();
      } else {
        setFormError(result.message || 'Could not save the scheme.');
      }
    } catch (e) {
      setFormError('Something went wrong while saving.');
    } finally {
      setSaving(false);
    }
  };

  // ---- quick active / inactive toggle ----
  const toggleStatus = async (row) => {
    const newStatus = String(row.status) === '1' ? '2' : '1';

    try {
      const response = await fetch(`${config.API_BASE_URL}masters/cu_scheme_master`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData({
          id: row.id,
          scheme_category: row.scheme_category,
          status: newStatus,
          user_id: userId,
        }),
      });

      const result = await response.json();
      if (result.success) {
        getSchemes();
      }
    } catch (e) {
      // ignore - could add a toast here
    }
  };

  const categoryLabel = (key) => SCHEME_CATEGORIES.find((c) => c.key === key)?.label || key || 'N/A';

  return (
    <>
      <style>{`
      .bureau-score-option:hover {
        background-color: #f1f3f5;
      }
      .bureau-score-selected {
        background-color: #e7f1ff;
      }
      .bureau-score-selected:hover {
        background-color: #d7e8ff;
      }
    `}</style>

      <Container maxWidth="xl">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <Typography variant="h4">Scheme Master</Typography>
          <Button variant="contained" onClick={openAddModal}>
            + Add Scheme
          </Button>
        </div>

        <div className="d-flex align-items-center gap-2 mb-3">
          <label className="fw-bold mb-0 me-2">Category</label>
          <select
            className="form-select w-auto"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All</option>
            {SCHEME_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {error && <div className="text-danger mb-3">{error}</div>}

        <div className="table-wrap">
          <table className="table table-hover table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Category</th>
                <th>OEM</th>
                <th>Cust. Category</th>
                <th>City</th>
                <th>Type</th>
                <th>Tenure</th>
                <th>LTV</th>
                <th>Std Rate (Flat)</th>
                <th>Std Rate (Red.)</th>
                <th>Bureau Score</th>
                <th>PF</th>
                <th>Doc Charges</th>
                <th>KLI</th>
                <th>Status</th>
                <th style={{ width: 110 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={16} className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : schemes.length === 0 ? (
                <tr>
                  <td colSpan={16} className="text-center py-4">
                    No schemes found.
                  </td>
                </tr>
              ) : (
                schemes.map((row) => (
                  <tr key={row.id}>
                    <td>{row.formatetdID || row.id}</td>
                    <td>{categoryLabel(row.scheme_category)}</td>
                    <td>{row.oemName || 'N/A'}</td>
                    <td>{row.customer_category || 'N/A'}</td>
                    <td>{row.city || 'N/A'}</td>
                    <td>{row.type || 'N/A'}</td>
                    <td>{row.tenure || 'N/A'}</td>
                    <td>{row.ltv ?? 'N/A'}</td>
                    <td>{row.standard_rate_flat ?? 'N/A'}</td>
                    <td>{row.standard_rate_reducing ?? 'N/A'}</td>
                    <td>{row.bureau_score || 'N/A'}</td>
                    <td>{row.pf ?? 'N/A'}</td>
                    <td>{row.document_charges || 'N/A'}</td>
                    <td>{row.kli || 'N/A'}</td>
                    <td>
                      <span className={row.statusName === 'Active' ? 'badge bg-success' : 'badge bg-danger'}>
                        {row.statusName === 'Active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title="Edit"
                          onClick={() => openEditModal(row)}
                        >
                          <i className="bi bi-pencil-square" />
                        </button>
                        {/* <button
                          type="button"
                          className={`btn btn-sm ${
                            String(row.status) === '1' ? 'btn-outline-danger' : 'btn-outline-success'
                          }`}
                          title={String(row.status) === '1' ? 'Deactivate' : 'Activate'}
                          onClick={() => toggleStatus(row)}
                        >
                          <i className={String(row.status) === '1' ? 'bi bi-toggle-off' : 'bi bi-toggle-on'} />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Container>

      <Modal open={showModal} onClose={closeModal}>
        <Box
          sx={{
            width: 900,
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: '#fff',
            p: 3,
            borderRadius: 2,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">{form.id ? 'Edit Scheme' : 'Add Scheme'}</h4>
            <button type="button" className="btn-close" onClick={closeModal} />
          </div>

          {formError && <div className="text-danger mb-3">{formError}</div>}

          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-bold">Scheme Category</label>
              <select
                name="scheme_category"
                className="form-select"
                value={form.scheme_category}
                onChange={handleChange}
              >
                {SCHEME_CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">OEM</label>
              <select name="oem_id" className="form-select" value={form.oem_id} onChange={handleChange}>
                <option value="">Select OEM</option>
                {oems.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.oem_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Customer Category</label>
              <input
                type="text"
                name="customer_category"
                className="form-control"
                value={form.customer_category}
                onChange={handleChange}
                placeholder="Captive / DCO"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">City</label>
              <input
                type="text"
                name="city"
                className="form-control"
                value={form.city}
                onChange={handleChange}
                placeholder="e.g. Mumbai"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Type</label>
              <input
                type="text"
                name="type"
                className="form-control"
                value={form.type}
                onChange={handleChange}
                placeholder="Owned House / Rented"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Tenure (months)</label>
              <input type="number" name="tenure" className="form-control" value={form.tenure} onChange={handleChange} />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">LTV</label>
              <input
                type="number"
                step="0.0001"
                name="ltv"
                className="form-control"
                value={form.ltv}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Old Rate</label>
              <input
                type="number"
                step="0.0001"
                name="old_rate"
                className="form-control"
                value={form.old_rate}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Increase</label>
              <input
                type="number"
                step="0.0001"
                name="increase"
                className="form-control"
                value={form.increase}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Standard Rate (Flat)</label>
              <input
                type="number"
                step="0.0001"
                name="standard_rate_flat"
                className="form-control"
                value={form.standard_rate_flat}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Standard Rate (Reducing)</label>
              <input
                type="number"
                step="0.0001"
                name="standard_rate_reducing"
                className="form-control"
                value={form.standard_rate_reducing}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4 position-relative">
              <label className="form-label fw-bold">Bureau Score</label>

              <button
                type="button"
                className="form-control d-flex justify-content-between align-items-center text-start"
                onClick={() => {
                  setBureauDropdownOpen((prev) => !prev);
                  setBureauSearch('');
                }}
              >
                <span className={form.bureau_score ? '' : 'text-muted'}>
                  {form.bureau_score || 'Select Bureau Score'}
                </span>
                <i className={`bi ${bureauDropdownOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
              </button>

              {bureauDropdownOpen && (
                <div
                  className="shadow bg-white border rounded"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    marginTop: '2px',
                    maxHeight: '260px',
                    overflowY: 'auto',
                  }}
                >
                  <div className="p-2 border-bottom">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search score..."
                      value={bureauSearch}
                      onChange={(e) => setBureauSearch(e.target.value)}
                    />
                  </div>

                  <ul className="list-unstyled mb-0">
                    {BUREAU_SCORE_OPTIONS.filter((score) => score.includes(bureauSearch)).length === 0 ? (
                      <li className="px-3 py-2 text-muted small">No match found</li>
                    ) : (
                      BUREAU_SCORE_OPTIONS.filter((score) => score.includes(bureauSearch)).map((score) => (
                        <li key={score} className="p-0">
                          <button
                            type="button"
                            className={`bureau-score-option w-100 text-start border-0 px-3 py-2 bg-transparent ${
                              form.bureau_score === score ? 'bureau-score-selected' : ''
                            }`}
                            onClick={() => {
                              setForm((prev) => ({ ...prev, bureau_score: score }));
                              setBureauDropdownOpen(false);
                              setBureauSearch('');
                            }}
                          >
                            {score}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Bureau Impact</label>
              <input
                type="number"
                step="0.0001"
                name="bureau_impact"
                className="form-control"
                value={form.bureau_impact}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Applicable Rate (Flat)</label>
              <input
                type="number"
                step="0.0001"
                name="applicable_rate_flat"
                className="form-control"
                value={form.applicable_rate_flat}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Applicable Rate (Reducing)</label>
              <input
                type="number"
                step="0.0001"
                name="applicable_rate_reducing"
                className="form-control"
                value={form.applicable_rate_reducing}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">PF</label>
              <input
                type="number"
                step="0.0001"
                name="pf"
                className="form-control"
                value={form.pf}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Document Charges</label>
              <input
                type="text"
                name="document_charges"
                className="form-control"
                value={form.document_charges}
                onChange={handleChange}
                placeholder="Rs 1000"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">KLI</label>
              <input
                type="text"
                name="kli"
                className="form-control"
                value={form.kli}
                onChange={handleChange}
                placeholder="Mandatory"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">IRR (Deviation only)</label>
              <input
                type="number"
                step="0.0001"
                name="irr"
                className="form-control"
                value={form.irr}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Effective From</label>
              <input
                type="date"
                name="effective_from"
                className="form-control"
                value={form.effective_from}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Effective To</label>
              <input
                type="date"
                name="effective_to"
                className="form-control"
                value={form.effective_to}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold">Status</label>
              <select name="status" className="form-select" value={form.status} onChange={handleChange}>
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </select>
            </div>

            <div className="col-12">
              <label className="form-label fw-bold">Remarks</label>
              <textarea name="remarks" className="form-control" rows={2} value={form.remarks} onChange={handleChange} />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outlined" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default SchemeMaster;